// ---------------------------------------------------------------------------
// EvaluationRunsService — HTTP-backed implementation against WorkflowSvc.
//
// Talks to /workspaces/{workspaceName}/evaluation/evaluation-runs. v1 of the
// detail page only needs queue (POST) + get + cancel; list / result-download
// will land when a runs page exists.
// ---------------------------------------------------------------------------

import type { IHttpClient } from '../api/client.js';
import { AppError } from '../api/errors.js';
import type {
  EvaluationRun,
  QueueEvaluationRunBody,
} from '../types/evaluationRun.js';

import type {
  IEvaluationRunsService,
  ListEvaluationRunsOptions,
} from './evaluationRunsService.interface.js';
import { EvaluationRunNotFoundError } from './evaluationRunsService.interface.js';

interface ListResponseEnvelope {
  value: EvaluationRun[];
  nextLink?: string;
}

function basePath(workspaceName: string): string {
  return `workspaces/${encodeURIComponent(workspaceName)}/evaluation/evaluation-runs`;
}

function runPath(workspaceName: string, id: string): string {
  return `${basePath(workspaceName)}/${encodeURIComponent(id)}`;
}

function isHttpStatus(err: unknown, status: number): boolean {
  return err instanceof AppError && err.statusCode === status;
}

export class EvaluationRunsService implements IEvaluationRunsService {
  constructor(private readonly client: IHttpClient) {}

  async queue(
    workspaceName: string,
    body: QueueEvaluationRunBody,
  ): Promise<EvaluationRun> {
    const { data } = await this.client.post<EvaluationRun>(
      basePath(workspaceName),
      body,
    );
    return data;
  }

  async get(workspaceName: string, id: string): Promise<EvaluationRun> {
    try {
      const { data } = await this.client.get<EvaluationRun>(
        runPath(workspaceName, id),
      );
      return data;
    } catch (err) {
      if (isHttpStatus(err, 404)) {
        throw new EvaluationRunNotFoundError(workspaceName, id);
      }
      throw err;
    }
  }

  async list(
    workspaceName: string,
    options?: ListEvaluationRunsOptions,
  ): Promise<EvaluationRun[]> {
    const params = new URLSearchParams();
    if (options?.displayNamePrefix !== undefined) {
      // Server's OData parser supports startswith(displayName, '...') only.
      // Single-quote any single quotes in the prefix per OData rules.
      const escaped = options.displayNamePrefix.replace(/'/g, "''");
      params.set('$filter', `startswith(displayName,'${escaped}')`);
    }
    // `createdDate` lives on the server's nested `systemProperties` complex
    // type, not as a top-level EDM property — ordering by the flat name 400s.
    // Use the nested path (matches workflowApi's eval-runs list query).
    params.set('$orderby', 'systemProperties/createdDate desc');
    if (options?.top !== undefined) {
      params.set('$top', String(options.top));
    }
    if (options?.skip !== undefined) {
      params.set('$skip', String(options.skip));
    }
    const query = params.toString();
    const url = query
      ? `${basePath(workspaceName)}?${query}`
      : basePath(workspaceName);
    const { data } = await this.client.get<ListResponseEnvelope>(url);
    return data.value;
  }

  async getReportHtml(workspaceName: string, ids: string[]): Promise<string> {
    // Mirrors workflowApi's report route: repeated `ids` params, text/html
    // response retrieved via the authenticated client (Bearer token).
    const params = new URLSearchParams(ids.map((id) => ['ids', id]));
    const url = `${basePath(workspaceName)}/report?${params.toString()}`;
    const { data } = await this.client.getText(url);
    return data;
  }

  async cancel(workspaceName: string, id: string): Promise<EvaluationRun> {
    try {
      const { data } = await this.client.post<EvaluationRun>(
        `${runPath(workspaceName, id)}:cancel`,
      );
      return data;
    } catch (err) {
      if (isHttpStatus(err, 404)) {
        throw new EvaluationRunNotFoundError(workspaceName, id);
      }
      throw err;
    }
  }
}
